import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { getUser } from '../auth';

const filters = ['🌿 Tout', '🌲 Nature', '⚽ Sport', '🎨 Créatif', '🏃 Jeux'];

const colorMap = {
  'Nature': '#EDF7F1', 'Sport': '#FFF3EE',
  'Créatif': '#EEF4FF', 'Jeux': '#FFFBE6',
  'Cuisine': '#FFF3EE', 'Culture': '#F3E5F5',
};

const getDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
};

const formatDist = (km) => km < 1 ? Math.round(km * 1000) + ' m' : km.toFixed(1) + ' km';

const getVille = (lieu) => {
  if (!lieu) return '';
  const parts = lieu.split(',');
  return parts.length > 1 ? parts[1]?.trim() : parts[0]?.trim();
};

const DATE_FILTERS = [
  { key: 'tout', label: '📅 Tout' },
  { key: 'aujourd_hui', label: "Aujourd'hui" },
  { key: 'demain', label: 'Demain' },
  { key: 'semaine', label: 'Cette semaine' },
  { key: 'passes', label: 'Passées' },
];

const AGE_FILTERS = [
  { key: 'tout', label: 'Tous âges' },
  { key: '0-3', label: '0–3 ans', min: 0, max: 3 },
  { key: '4-6', label: '4–6 ans', min: 4, max: 6 },
  { key: '7-10', label: '7–10 ans', min: 7, max: 10 },
  { key: '11-18', label: '11+ ans', min: 11, max: 18 },
];

export default function Feed() {
  const navigate = useNavigate();
  const user = getUser();
  const [activeFilter, setActiveFilter] = useState('🌿 Tout');
  const [dateFilter, setDateFilter] = useState('tout');
  const [ageFilter, setAgeFilter] = useState('tout');
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userPos, setUserPos] = useState(null);
  const [rayon, setRayon] = useState(15);
  const [showRayon, setShowRayon] = useState(false);
  const [showAgeFilter, setShowAgeFilter] = useState(false);
  const [showApropos, setShowApropos] = useState(false);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setUserPos({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
        () => {},
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    }
    fetchActivites();
  }, []);

  const fetchActivites = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('activites')
      .select('*, users(prenom, avatar_url, is_official)')
      .order('date', { ascending: true });
    if (!error && data) {
      const avecInfos = await Promise.all(data.map(async (a) => {
        const [{ data: notes }, { count: nb }] = await Promise.all([
          supabase.from('notations').select('note').eq('activite_id', a.id),
          supabase.from('participations').select('*', { count: 'exact', head: true }).eq('activite_id', a.id),
        ]);
        const moyenne = notes && notes.length > 0 ? (notes.reduce((s, n) => s + n.note, 0) / notes.length).toFixed(1) : null;
        return { ...a, note_moyenne: moyenne, nb_participants: nb || 0 };
      }));
      setActivities(avecInfos);
    }
    setLoading(false);
  };

  const filterByDate = (a) => {
    const today = new Date(); today.setHours(0,0,0,0);
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    const nextWeek = new Date(today); nextWeek.setDate(nextWeek.getDate() + 7);
    const d = new Date(a.date); d.setHours(0,0,0,0);
    switch (dateFilter) {
      case 'aujourd_hui': return d.getTime() === today.getTime();
      case 'demain': return d.getTime() === tomorrow.getTime();
      case 'semaine': return d >= today && d < nextWeek;
      case 'passes': return d < today;
      default: return d >= today;
    }
  };

  const filterByAge = (a) => {
    if (ageFilter === 'tout') return true;
    const f = AGE_FILTERS.find(f => f.key === ageFilter);
    return f ? a.age_min <= f.max && a.age_max >= f.min : true;
  };

  const withDist = activities.map(a => ({
    ...a,
    dist: (userPos && a.latitude && a.longitude) ? getDistance(userPos.lat, userPos.lon, a.latitude, a.longitude) : null,
  })).filter(a => {
    if (!userPos || a.dist === null || dateFilter === 'passes') return true;
    return a.dist <= rayon;
  }).filter(filterByDate).filter(filterByAge)
    .filter(a => activeFilter === '🌿 Tout' || activeFilter.includes(a.type))
    .sort((a, b) => {
      if (a.users?.is_official && !b.users?.is_official) return -1;
      if (!a.users?.is_official && b.users?.is_official) return 1;
      if (a.dist !== null && b.dist !== null) return a.dist - b.dist;
      if (a.dist !== null && b.dist === null) return -1;
      if (a.dist === null && b.dist !== null) return 1;
      return new Date(a.date) - new Date(b.date);
    });

  const today = new Date(); today.setHours(0,0,0,0);
  const dimanche = new Date(today); dimanche.setDate(today.getDate() + (7 - today.getDay()));
  const weekend = withDist.filter(a => {
    const d = new Date(a.date); d.setHours(0,0,0,0);
    return d >= today && d <= dimanche;
  }).slice(0, 6);

  const heure = new Date().getHours();
  const salutation = heure < 12 ? 'Bonjour' : heure < 18 ? 'Bon après-midi' : 'Bonsoir';
  const ageFilterActif = AGE_FILTERS.find(f => f.key === ageFilter);

  const CardWooki = () => (
    <div style={{ background: 'linear-gradient(135deg, #1C2B1E, #2D7A52)', borderRadius: 24, padding: '20px', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{ fontSize: '2rem' }}>🌿</div>
        <div>
          <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1rem', color: 'white', marginBottom: 2 }}>C'est quoi Wooki ?</div>
          <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>
            Des familles de ton quartier organisent des sorties ensemble — balade, foot, pique-nique... Rejoins-les ou propose la tienne !
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {['🆓 Gratuit', '🔒 Sécurisé', '👨‍👩‍👧 Entre familles', '📍 Près de toi'].map((b, i) => (
          <span key={i} style={{ background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', padding: '4px 10px', borderRadius: 100, fontSize: '0.72rem', fontWeight: 700 }}>{b}</span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => navigate('/inscription')} style={{ flex: 1, padding: '12px', background: '#3D9A6A', color: 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer' }}>
          Rejoindre gratuitement →
        </button>
        <button onClick={() => navigate('/connexion')} style={{ padding: '12px 16px', background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          J'ai un compte
        </button>
      </div>
    </div>
  );

  const ModalApropos = () => (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }} onClick={() => setShowApropos(false)}>
      <div style={{ background: 'white', borderRadius: '28px 28px 0 0', padding: '28px 24px 48px', width: '100%', maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.8rem', marginBottom: 4 }}>
            <span style={{ color: '#3D9A6A' }}>Woo</span><span style={{ color: '#FF7043' }}>ki</span>
          </div>
          <div style={{ fontSize: '0.82rem', color: '#8A9E8D' }}>Sorties en famille 🌿</div>
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1rem', color: '#1C2B1E', marginBottom: 12 }}>Comment ça marche ?</div>
          {[
            { num: '1', titre: 'Explore les activités', desc: 'Vois les sorties organisées par des familles près de chez toi.' },
            { num: '2', titre: 'Rejoins ou crée', desc: 'Inscris-toi à une sortie ou propose la tienne en 2 minutes.' },
            { num: '3', titre: 'Rencontre des familles', desc: 'Retrouve-toi en plein air avec d\'autres familles du quartier !' },
          ].map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 10 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#3D9A6A', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.85rem', color: 'white' }}>{s.num}</span>
              </div>
              <div>
                <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.88rem', color: '#1C2B1E', marginBottom: 2 }}>{s.titre}</div>
                <div style={{ fontSize: '0.78rem', color: '#8A9E8D', lineHeight: 1.5 }}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ background: '#EDF7F1', borderRadius: 16, padding: '12px 14px', marginBottom: 20 }}>
          <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.9rem', color: '#1C2B1E', marginBottom: 8 }}>🔒 Sécurité des enfants</div>
          {['👨‍👩‍👧 Parents présents obligatoires', '🚫 Zéro échange de coordonnées', '✅ Charte signée par chaque organisateur', '🆓 Gratuit, sans pub'].map((b, i) => (
            <div key={i} style={{ fontSize: '0.78rem', color: '#4A6355', padding: '4px 0', borderBottom: i < 3 ? '1px solid #D4EEE0' : 'none' }}>{b}</div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <button onClick={() => { setShowApropos(false); navigate('/cgu'); }} style={{ flex: 1, padding: '11px', background: '#F3F4F2', color: '#4A6355', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>📋 CGU</button>
          <button onClick={() => { setShowApropos(false); navigate('/pro'); }} style={{ flex: 1, padding: '11px', background: '#FFF3EE', color: '#FF8F00', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>🎯 Animateurs</button>
          <button onClick={() => setShowApropos(false)} style={{ flex: 1, padding: '11px', background: '#F3F4F2', color: '#4A6355', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>✉️ Contact</button>
        </div>
        <button onClick={() => setShowApropos(false)} style={{ width: '100%', padding: '14px', background: '#3D9A6A', color: 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer' }}>Fermer</button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#FAFAF7', paddingBottom: 100 }}>
      <style>{`
        @keyframes cardSlideUp{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}
        @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @keyframes urgentPulse{0%,100%{opacity:1}50%{opacity:0.6}}
        @keyframes newBadge{0%{transform:scale(0)}70%{transform:scale(1.1)}100%{transform:scale(1)}}
      `}</style>

      {showApropos && <ModalApropos />}

      {/* MODAL RAYON */}
      {showRayon && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 500, backdropFilter: 'blur(4px)' }} onClick={() => setShowRayon(false)}>
          <div style={{ position: 'absolute', bottom: 90, left: '50%', transform: 'translateX(-50%)', width: '90%', maxWidth: 380, background: 'white', borderRadius: 28, padding: '24px' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.1rem', color: '#1C2B1E', marginBottom: 4 }}>Rayon de recherche</div>
            <div style={{ fontSize: '0.82rem', color: '#8A9E8D', marginBottom: 20 }}>Activités dans <strong style={{ color: '#3D9A6A' }}>{rayon} km</strong> autour de toi</div>
            <input type="range" min="5" max="100" step="5" value={rayon} onChange={e => setRayon(parseInt(e.target.value))} style={{ width: '100%', accentColor: '#3D9A6A', marginBottom: 8 }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#8A9E8D', marginBottom: 16 }}>
              <span>5 km</span><span>50 km</span><span>100 km</span>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              {[5, 10, 15, 20, 50, 100].map(r => (
                <button key={r} onClick={() => setRayon(r)} style={{ flex: 1, padding: '8px 0', background: rayon === r ? '#3D9A6A' : '#F3F4F2', color: rayon === r ? 'white' : '#8A9E8D', border: 'none', borderRadius: 10, fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer' }}>{r}</button>
              ))}
            </div>
            <button onClick={() => setShowRayon(false)} style={{ width: '100%', padding: '14px', background: '#3D9A6A', color: 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer' }}>✅ Appliquer</button>
          </div>
        </div>
      )}

      {/* MODAL ÂGE */}
      {showAgeFilter && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 500, backdropFilter: 'blur(4px)' }} onClick={() => setShowAgeFilter(false)}>
          <div style={{ position: 'absolute', bottom: 90, left: '50%', transform: 'translateX(-50%)', width: '90%', maxWidth: 380, background: 'white', borderRadius: 28, padding: '24px' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.1rem', color: '#1C2B1E', marginBottom: 16 }}>Âge des enfants 👶</div>
            {AGE_FILTERS.map(f => (
              <div key={f.key} onClick={() => { setAgeFilter(f.key); setShowAgeFilter(false); }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: ageFilter === f.key ? '#EDF7F1' : '#FAFAF7', borderRadius: 16, marginBottom: 8, cursor: 'pointer', border: ageFilter === f.key ? '2px solid #3D9A6A' : '2px solid #E8EFE9' }}>
                <div style={{ fontWeight: 700, color: '#1C2B1E' }}>{f.label}</div>
                {ageFilter === f.key && <div style={{ color: '#3D9A6A', fontWeight: 800 }}>✓</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* HEADER */}
      <div style={{ position: 'sticky', top: 0, zIndex: 100, background: 'white', borderBottom: '1px solid #EEF2EE', padding: '16px 20px 0', boxShadow: '0 2px 16px rgba(28,43,30,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.3rem', color: '#1C2B1E' }}>
              {user ? `${salutation} ${user.prenom} 👋` : '🌿 Sorties près de toi'}
            </div>
            {userPos ? (
              <div onClick={() => setShowRayon(true)} style={{ fontSize: '0.72rem', color: '#3D9A6A', fontWeight: 700, marginTop: 3, cursor: 'pointer' }}>
                📍 {rayon} km · <span style={{ textDecoration: 'underline' }}>Modifier</span>
              </div>
            ) : (
              <div style={{ fontSize: '0.72rem', color: '#8A9E8D', marginTop: 3 }}>📍 Localisation en cours...</div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {!user && (
              <button onClick={() => navigate('/inscription')} style={{ background: '#3D9A6A', color: 'white', border: 'none', borderRadius: 100, padding: '7px 14px', fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '0.75rem', cursor: 'pointer' }}>
                Rejoindre
              </button>
            )}
            <div onClick={() => navigate('/feed')} style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.4rem', cursor: 'pointer' }}>
              <span style={{ color: '#3D9A6A' }}>Woo</span><span style={{ color: '#FF7043' }}>ki</span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, scrollbarWidth: 'none' }}>
          {DATE_FILTERS.map(f => (
            <button key={f.key} onClick={() => setDateFilter(f.key)} style={{ flexShrink: 0, padding: '6px 14px', borderRadius: 100, fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', border: 'none', background: dateFilter === f.key ? '#FF7043' : '#F3F4F2', color: dateFilter === f.key ? 'white' : '#8A9E8D', transition: 'all 0.2s' }}>{f.label}</button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 14, scrollbarWidth: 'none' }}>
          {filters.map(f => (
            <button key={f} onClick={() => setActiveFilter(f)} style={{ flexShrink: 0, padding: '7px 14px', borderRadius: 100, fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', border: 'none', background: activeFilter === f ? '#1C2B1E' : '#F3F4F2', color: activeFilter === f ? 'white' : '#8A9E8D', transition: 'all 0.2s' }}>{f}</button>
          ))}
          <button onClick={() => setShowAgeFilter(true)} style={{ flexShrink: 0, padding: '7px 14px', borderRadius: 100, fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', border: 'none', background: ageFilter !== 'tout' ? '#9C27B0' : '#F3F4F2', color: ageFilter !== 'tout' ? 'white' : '#8A9E8D', transition: 'all 0.2s' }}>
            👶 {ageFilter !== 'tout' ? ageFilterActif?.label : 'Âge'}
          </button>
        </div>
      </div>

      <div style={{ padding: '16px 20px' }}>

        {loading && (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 12, animation: 'spin 1.5s linear infinite', display: 'inline-block' }}>🌿</div>
            <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1rem', color: '#4A6355' }}>Chargement...</div>
          </div>
        )}

        {/* CE WEEKEND */}
        {!loading && weekend.length > 0 && dateFilter === 'tout' && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.1rem', color: '#1C2B1E' }}>🌟 Ce weekend</div>
              <div style={{ fontSize: '0.72rem', color: '#8A9E8D', fontWeight: 700 }}>{weekend.length} activité{weekend.length > 1 ? 's' : ''}</div>
            </div>
            <div style={{ display: 'flex', gap: 12, overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: 4 }}>
              {weekend.map((a, i) => {
                const complet = a.nb_participants >= a.max_familles;
                const ville = getVille(a.lieu);
                const isNew = !!(a.created_at && (new Date() - new Date(a.created_at)) < 24 * 60 * 60 * 1000);
                return (
                  <div key={a.id} onClick={() => navigate(`/detail/${a.id}`)}
                    style={{ flexShrink: 0, width: 160, borderRadius: 20, overflow: 'hidden', cursor: 'pointer', boxShadow: '0 4px 16px rgba(28,43,30,0.12)', animation: `cardSlideUp 0.4s ease ${i * 0.08}s both` }}>
                    {a.photo_url ? (
                      <div style={{ height: 100, position: 'relative' }}>
                        <img src={a.photo_url} alt={a.titre} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 40%, rgba(28,43,30,0.6))' }} />
                        {a.users?.is_official && <div style={{ position: 'absolute', top: 6, left: 6, background: '#FFB300', borderRadius: 100, padding: '2px 7px', fontSize: '0.6rem', fontWeight: 800, color: 'white' }}>⭐</div>}
                        {isNew && <div style={{ position: 'absolute', top: 6, right: 6, background: '#3D9A6A', borderRadius: 100, padding: '2px 7px', fontSize: '0.6rem', fontWeight: 800, color: 'white' }}>🆕</div>}
                      </div>
                    ) : (
                      <div style={{ height: 100, background: colorMap[a.type] || '#EDF7F1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.8rem', position: 'relative' }}>
                        {a.emoji}
                        {a.users?.is_official && <div style={{ position: 'absolute', top: 6, left: 6, background: '#FFB300', borderRadius: 100, padding: '2px 7px', fontSize: '0.6rem', fontWeight: 800, color: 'white' }}>⭐</div>}
                        {isNew && <div style={{ position: 'absolute', top: 6, right: 6, background: '#3D9A6A', borderRadius: 100, padding: '2px 7px', fontSize: '0.6rem', fontWeight: 800, color: 'white' }}>🆕</div>}
                      </div>
                    )}
                    <div style={{ background: 'white', padding: '10px 10px 12px' }}>
                      <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.82rem', color: '#1C2B1E', marginBottom: 4, lineHeight: 1.2, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{a.titre}</div>
                      <div style={{ fontSize: '0.68rem', color: '#8A9E8D', marginBottom: 6 }}>
                        {new Date(a.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' })} · {a.heure?.slice(0,5)}
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {a.dist !== null && (
                          <span style={{ background: '#EDF7F1', color: '#3D9A6A', padding: '2px 7px', borderRadius: 100, fontSize: '0.62rem', fontWeight: 800 }}>
                            📍 {formatDist(a.dist)}{ville ? ` · ${ville}` : ''}
                          </span>
                        )}
                        {complet && <span style={{ background: '#FFF3EE', color: '#FF7043', padding: '2px 7px', borderRadius: 100, fontSize: '0.62rem', fontWeight: 800 }}>Complet</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* TITRE */}
        {!loading && withDist.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.1rem', color: '#1C2B1E' }}>
              {dateFilter === 'passes' ? '📅 Passées' : dateFilter === 'aujourd_hui' ? "📅 Aujourd'hui" : dateFilter === 'demain' ? '📅 Demain' : '🌿 Toutes les activités'}
            </div>
            <div style={{ fontSize: '0.72rem', color: '#8A9E8D', fontWeight: 700 }}>{withDist.length} résultat{withDist.length > 1 ? 's' : ''}</div>
          </div>
        )}

        {/* LISTE VIDE */}
        {!loading && withDist.length === 0 && (
          <div style={{ background: 'linear-gradient(135deg, #1C2B1E, #2D7A52)', borderRadius: 28, padding: '36px 28px', textAlign: 'center' }}>
            <div style={{ fontSize: '4rem', marginBottom: 16 }}>🌱</div>
            <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.4rem', color: 'white', marginBottom: 10 }}>
              {ageFilter !== 'tout' ? `Pas d'activités pour ${ageFilterActif?.label}` : `Rien dans ${rayon} km !`}
            </div>
            <div style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.7, marginBottom: 24 }}>
              Élargis ton rayon ou sois le premier à organiser une sortie !
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => setRayon(50)} style={{ padding: '13px 24px', background: 'rgba(255,255,255,0.15)', color: 'white', border: '1.5px solid rgba(255,255,255,0.3)', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' }}>
                📍 Élargir à 50 km
              </button>
              <button onClick={() => user ? navigate('/creer') : navigate('/inscription')} style={{ padding: '13px 24px', background: '#FF7043', color: 'white', border: 'none', borderRadius: 100, fontFamily: 'Nunito, sans-serif', fontWeight: 900, fontSize: '0.88rem', cursor: 'pointer' }}>
                ＋ Créer une activité
              </button>
            </div>
          </div>
        )}

        {/* LISTE */}
        {!loading && withDist.map((a, index) => {
          const color = colorMap[a.type] || '#EDF7F1';
          const date = new Date(a.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
          const complet = a.nb_participants >= a.max_familles;
          const actDate = new Date(a.date); actDate.setHours(0,0,0,0);
          const todayD = new Date(); todayD.setHours(0,0,0,0);
          const tomorrowD = new Date(todayD); tomorrowD.setDate(tomorrowD.getDate() + 1);
          const isToday = actDate.getTime() === todayD.getTime();
          const isTomorrow = actDate.getTime() === tomorrowD.getTime();
          const isPasse = actDate < todayD;
          const isOfficial = a.users?.is_official;
          const isUrgent = !complet && !isPasse && (a.max_familles - a.nb_participants) <= 2;
          const isNew = !isPasse && !!(a.created_at && (new Date() - new Date(a.created_at)) < 24 * 60 * 60 * 1000);
          const ville = getVille(a.lieu);
          const isPro = a.is_payante && a.prix_enfant > 0;

          const cardActivite = a.photo_url ? (
            <div key={a.id} onClick={() => navigate(`/detail/${a.id}`)}
              style={{ borderRadius: 24, marginBottom: 16, overflow: 'hidden', boxShadow: isOfficial ? '0 8px 32px rgba(255,179,0,0.2)' : '0 6px 24px rgba(28,43,30,0.12)', cursor: 'pointer', position: 'relative', height: 240, opacity: isPasse ? 0.75 : 1, border: isOfficial ? '2px solid #FFB300' : 'none', animation: `cardSlideUp 0.4s ease ${Math.min(index * 0.05, 0.3)}s both` }}>
              <img src={a.photo_url} alt={a.titre} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, transparent 35%, rgba(28,43,30,0.88) 100%)' }} />
              <div style={{ position: 'absolute', top: 14, left: 14, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {isOfficial && <span style={{ background: '#FFB300', color: 'white', padding: '4px 10px', borderRadius: 100, fontSize: '0.7rem', fontWeight: 800 }}>⭐ Officiel</span>}
                {isPro && <span style={{ background: '#FF8F00', color: 'white', padding: '4px 10px', borderRadius: 100, fontSize: '0.7rem', fontWeight: 800 }}>💰 {a.prix_enfant}€/enfant</span>}
                {isNew && <span style={{ background: '#3D9A6A', color: 'white', padding: '4px 10px', borderRadius: 100, fontSize: '0.7rem', fontWeight: 800 }}>🆕</span>}
                {isToday && <span style={{ background: '#FF7043', color: 'white', padding: '4px 10px', borderRadius: 100, fontSize: '0.7rem', fontWeight: 800 }}>Aujourd'hui</span>}
                {isTomorrow && <span style={{ background: '#3D9A6A', color: 'white', padding: '4px 10px', borderRadius: 100, fontSize: '0.7rem', fontWeight: 800 }}>Demain</span>}
                {isPasse && <span style={{ background: 'rgba(0,0,0,0.5)', color: 'white', padding: '4px 10px', borderRadius: 100, fontSize: '0.7rem', fontWeight: 800 }}>Terminée</span>}
                {isUrgent && <span style={{ background: '#FF7043', color: 'white', padding: '4px 10px', borderRadius: 100, fontSize: '0.7rem', fontWeight: 800, animation: 'urgentPulse 1.5s ease-in-out infinite' }}>🔥 {a.max_familles - a.nb_participants} place{a.max_familles - a.nb_participants > 1 ? 's' : ''}</span>}
              </div>
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '14px 16px 16px' }}>
                <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.2rem', color: 'white', marginBottom: 6, lineHeight: 1.2 }}>{a.titre}</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
                  <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.8)' }}>
                    {isOfficial ? '⭐ Wooki' : a.users?.prenom} · {date} · {a.heure?.slice(0,5)}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <span style={{ background: 'rgba(255,255,255,0.18)', color: 'white', padding: '3px 10px', borderRadius: 100, fontSize: '0.7rem', fontWeight: 800 }}>
                      👨‍👩‍👧 {a.nb_participants}/{a.max_familles}
                    </span>
                    {a.dist !== null && (
                      <span style={{ background: 'rgba(255,255,255,0.18)', color: 'white', padding: '3px 10px', borderRadius: 100, fontSize: '0.7rem', fontWeight: 800 }}>
                        📍 {formatDist(a.dist)}{ville ? ` · ${ville}` : ''}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div key={a.id} onClick={() => navigate(`/detail/${a.id}`)} style={{
              background: isOfficial ? 'linear-gradient(135deg, #FFFDE7, #FFF9C4)' : isPro ? 'linear-gradient(135deg, #FFF8E1, #FFF3CD)' : 'white',
              borderRadius: 24, marginBottom: 12,
              boxShadow: isOfficial ? '0 4px 20px rgba(255,179,0,0.15)' : '0 4px 20px rgba(28,43,30,0.07)',
              cursor: 'pointer', opacity: isPasse ? 0.75 : 1,
              border: isOfficial ? '2px solid #FFB300' : isPro ? '2px solid #FFE082' : 'none',
              overflow: 'hidden',
              animation: `cardSlideUp 0.4s ease ${Math.min(index * 0.05, 0.3)}s both`,
            }}>
              <div style={{ padding: '14px 16px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ width: 56, height: 56, borderRadius: 18, background: isPasse ? '#F3F4F2' : isOfficial ? 'rgba(255,179,0,0.15)' : color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem', flexShrink: 0, position: 'relative' }}>
                  {a.emoji}
                  {a.users?.avatar_url && (
                    <div style={{ position: 'absolute', bottom: -4, right: -4, width: 22, height: 22, borderRadius: '50%', overflow: 'hidden', border: '2px solid white' }}>
                      <img src={a.users.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                    <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1rem', color: isPasse ? '#8A9E8D' : '#1C2B1E', lineHeight: 1.2, flex: 1 }}>{a.titre}</div>
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                      {isOfficial && <span style={{ background: '#FFB300', color: 'white', padding: '2px 8px', borderRadius: 100, fontSize: '0.6rem', fontWeight: 800 }}>⭐</span>}
                      {isPro && <span style={{ background: '#FF8F00', color: 'white', padding: '2px 8px', borderRadius: 100, fontSize: '0.6rem', fontWeight: 800 }}>💰</span>}
                      {isUrgent && <span style={{ background: '#FF7043', color: 'white', padding: '2px 8px', borderRadius: 100, fontSize: '0.6rem', fontWeight: 800, animation: 'urgentPulse 1.5s ease-in-out infinite' }}>🔥</span>}
                      {isNew && <span style={{ background: '#3D9A6A', color: 'white', padding: '2px 8px', borderRadius: 100, fontSize: '0.6rem', fontWeight: 800, animation: 'newBadge 0.4s ease both' }}>🆕</span>}
                    </div>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#8A9E8D', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                    <span>{isOfficial ? '⭐ Wooki' : a.users?.prenom || 'Anonyme'}</span>
                    <span style={{ color: '#D0D8D2' }}>·</span>
                    <span>{date}</span>
                    <span style={{ color: '#D0D8D2' }}>·</span>
                    <span>{a.heure?.slice(0,5)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    {isToday && <span style={{ background: '#FFF3EE', color: '#FF7043', padding: '3px 9px', borderRadius: 100, fontSize: '0.67rem', fontWeight: 800 }}>Aujourd'hui</span>}
                    {isTomorrow && <span style={{ background: '#EDF7F1', color: '#3D9A6A', padding: '3px 9px', borderRadius: 100, fontSize: '0.67rem', fontWeight: 800 }}>Demain</span>}
                    {isPasse && <span style={{ background: '#F3F4F2', color: '#8A9E8D', padding: '3px 9px', borderRadius: 100, fontSize: '0.67rem', fontWeight: 800 }}>Terminée</span>}
                    {complet && !isPasse && <span style={{ background: '#FFF3EE', color: '#FF7043', padding: '3px 9px', borderRadius: 100, fontSize: '0.67rem', fontWeight: 800 }}>Complet</span>}
                    {isPro && <span style={{ background: '#FFF8E1', color: '#FF8F00', padding: '3px 9px', borderRadius: 100, fontSize: '0.67rem', fontWeight: 800 }}>💰 {a.prix_enfant}€/enfant</span>}
                    {isNew && !isToday && !isTomorrow && <span style={{ background: '#EDF7F1', color: '#3D9A6A', padding: '3px 9px', borderRadius: 100, fontSize: '0.67rem', fontWeight: 800 }}>🆕 Nouveau</span>}
                    <span style={{ background: isPasse ? '#F3F4F2' : '#EDF7F1', color: isPasse ? '#8A9E8D' : '#3D9A6A', padding: '3px 9px', borderRadius: 100, fontSize: '0.67rem', fontWeight: 800 }}>
                      👶 {a.age_min}–{a.age_max} ans
                    </span>
                    <span style={{ background: '#F3F4F2', color: '#8A9E8D', padding: '3px 9px', borderRadius: 100, fontSize: '0.67rem', fontWeight: 800 }}>
                      👨‍👩‍👧 {a.nb_participants}/{a.max_familles}
                    </span>
                    {a.dist !== null && (
                      <span style={{ background: '#EDF7F1', color: '#3D9A6A', padding: '3px 9px', borderRadius: 100, fontSize: '0.67rem', fontWeight: 800 }}>
                        📍 {formatDist(a.dist)}{ville ? ` · ${ville}` : ''}
                      </span>
                    )}
                    {a.note_moyenne && (
                      <span style={{ background: '#FFFBE6', color: '#FFB300', padding: '3px 9px', borderRadius: 100, fontSize: '0.67rem', fontWeight: 800 }}>
                        ⭐ {a.note_moyenne}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: isPasse ? '#F3F4F2' : complet ? '#F3F4F2' : isOfficial ? '#FFB300' : isPro ? '#FF8F00' : '#3D9A6A', display: 'flex', alignItems: 'center', justifyContent: 'center', color: (isPasse || complet) ? '#8A9E8D' : 'white', fontSize: '0.9rem', flexShrink: 0, alignSelf: 'center' }}>→</div>
              </div>
            </div>
          );

          return (
            <React.Fragment key={a.id}>
              {cardActivite}
              {!user && index === 2 && <CardWooki />}
            </React.Fragment>
          );
        })}

        {/* CARD CRÉER */}
        {!loading && withDist.length > 0 && dateFilter !== 'passes' && (
          <div onClick={() => user ? navigate('/creer') : navigate('/inscription')} style={{ background: 'linear-gradient(135deg, #1C2B1E, #2D7A52)', borderRadius: 24, padding: '20px', cursor: 'pointer', textAlign: 'center', boxShadow: '0 6px 24px rgba(28,43,30,0.2)', marginTop: 8, marginBottom: 12 }}>
            <div style={{ fontSize: '2rem', marginBottom: 8 }}>＋</div>
            <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1.1rem', color: 'white', marginBottom: 4 }}>Organise une sortie !</div>
            <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)' }}>Retrouve d'autres familles près de toi 🌿</div>
          </div>
        )}

        {/* CARD ANIMATEUR */}
        {!loading && (
          <div onClick={() => navigate('/pro')} style={{ background: 'linear-gradient(135deg, #FF8F00, #FFB300)', borderRadius: 24, padding: '16px 20px', cursor: 'pointer', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ fontSize: '2rem' }}>🎯</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '0.95rem', color: 'white', marginBottom: 2 }}>Tu es animateur ou passionné ?</div>
              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.85)' }}>Propose tes activités aux familles — Gratuit</div>
            </div>
            <div style={{ color: 'white', fontSize: '1.2rem' }}>→</div>
          </div>
        )}

        {/* FOOTER */}
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #E8EFE9', textAlign: 'center' }}>
          <div style={{ fontFamily: 'Fredoka One, cursive', fontSize: '1rem', marginBottom: 8 }}>
            <span style={{ color: '#3D9A6A' }}>Woo</span><span style={{ color: '#FF7043' }}>ki</span>
          </div>
          <div style={{ fontSize: '0.72rem', color: '#8A9E8D', marginBottom: 12 }}>Sorties en famille · Gratuit · Sécurisé 🌿</div>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <span onClick={() => setShowApropos(true)} style={{ fontSize: '0.75rem', color: '#3D9A6A', cursor: 'pointer', fontWeight: 700 }}>❓ Comment ça marche</span>
            <span onClick={() => navigate('/cgu')} style={{ fontSize: '0.75rem', color: '#8A9E8D', cursor: 'pointer' }}>📋 CGU & Charte</span>
            <span onClick={() => navigate('/pro')} style={{ fontSize: '0.75rem', color: '#FF8F00', cursor: 'pointer', fontWeight: 700 }}>🎯 Devenir animateur</span>
            <span style={{ fontSize: '0.75rem', color: '#8A9E8D' }}>✉️ contact@wooki.app</span>
          </div>
        </div>

      </div>
    </div>
  );
}